import { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Clock, X, InfoIcon, HelpCircle } from "lucide-react";
import { format, isAfter, addMonths } from "date-fns";
import { Checkbox } from "@/components/ui/checkbox";
import { DateRange } from "react-day-picker";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TimeSlot } from "./types";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";

interface AvailabilitySelectorProps {
  selectedSlots: TimeSlot[];
  onSlotsChange: (slots: TimeSlot[]) => void;
  hasLimitedPortions?: boolean;
  availablePortions?: string;
  onHasLimitedPortionsChange?: (value: boolean) => void;
  onAvailablePortionsChange?: (value: string) => void;
}

export const AvailabilitySelector = ({
  selectedSlots,
  onSlotsChange,
  hasLimitedPortions = false,
  availablePortions = "",
  onHasLimitedPortionsChange,
  onAvailablePortionsChange,
}: AvailabilitySelectorProps) => {
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [startTime, setStartTime] = useState("9:00 AM");
  const [endTime, setEndTime] = useState("10:00 AM");
  const [portions, setPortions] = useState("10");
  const [isRecurring, setIsRecurring] = useState(false);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [recurringEndDate, setRecurringEndDate] = useState<Date>(addMonths(new Date(), 1));

  const daysOfWeek = [
    "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"
  ];

  const handleAddSlot = () => {
    if (!dateRange?.from) return;

    const newSlot: TimeSlot = {
      dateRange: {
        from: dateRange.from,
        to: dateRange.to || dateRange.from
      },
      startTime,
      endTime,
      availablePortions: hasLimitedPortions ? parseInt(portions) : 0,
      ...(isRecurring && {
        recurring: {
          days: selectedDays,
          until: recurringEndDate
        }
      })
    };

    onSlotsChange([...selectedSlots, newSlot]);
    setDateRange(undefined);
    setSelectedDays([]);
    setIsRecurring(false);
  };

  const handleRemoveSlot = (index: number) => {
    const newSlots = [...selectedSlots];
    newSlots.splice(index, 1);
    onSlotsChange(newSlots);
  };

  const formatSlotDisplay = (slot: TimeSlot) => {
    if (slot.recurring) {
      const daysString = slot.recurring.days.join(", ");
      return `Every ${daysString} until ${format(new Date(slot.recurring.until), "MMM d, yyyy")}`;
    }
    return formatDateRange(slot);
  };

  const formatDateRange = (slot: TimeSlot) => {
    const { from, to } = slot.dateRange;
    if (!to || format(from, 'MMM d, yyyy') === format(to, 'MMM d, yyyy')) {
      return format(from, "MMM d, yyyy");
    }
    return `${format(from, "MMM d")}-${format(to, "d, yyyy")}`;
  };

  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const periodLabel = hour < 12 ? 'AM' : 'PM';
        let displayHour = hour % 12;
        if (displayHour === 0) displayHour = 12;
        const formattedMinute = minute.toString().padStart(2, "0");
        times.push(`${displayHour}:${formattedMinute} ${periodLabel}`);
      }
    }
    return times;
  };

  const renderTooltip = (label: string, content: string) => (
    <span className="inline-flex items-center space-x-1">
      <span>{label}</span>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            className="h-4 w-4 p-0 hover:bg-transparent"
          >
            <InfoIcon className="h-4 w-4 text-gray-500" />
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="max-w-[280px] text-sm bg-white p-2 rounded-lg shadow-lg z-[60] break-words"
          side="top"
          align="center"
          sideOffset={5}
        >
          <p>{content}</p>
        </PopoverContent>
      </Popover>
    </span>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label className="text-black mb-4 block text-xl">
            {renderTooltip(
              "Food Availability",
              "Select the dates when your dish will be available for order. You can add multiple time slots."
            )}
          </Label>
          <div className="border rounded-md overflow-hidden">
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={setDateRange}
              disabled={(date) =>
                date < new Date() || selectedSlots.some(slot => {
                  const slotStart = format(slot.dateRange.from, 'yyyy-MM-dd');
                  const slotEnd = slot.dateRange.to ? format(slot.dateRange.to, 'yyyy-MM-dd') : slotStart;
                  const currentDate = format(date, 'yyyy-MM-dd');
                  return currentDate >= slotStart && currentDate <= slotEnd;
                })
              }
              classNames={{
                day_range_end: "!bg-black !text-white hover:!bg-black hover:!text-white !rounded-r-md",
                day_range_start: "!bg-black !text-white hover:!bg-black hover:!text-white !rounded-l-md",
                day_selected: "!bg-black !text-white hover:!bg-black hover:!text-white",
                day_range_middle: "!bg-neutral-100 !text-black !rounded-none",
                day_today: "text-black",
              }}
            />
          </div>

          <div className="mt-4 space-y-2">
            <div className="flex items-center space-x-2">
              <Switch
                checked={isRecurring}
                onCheckedChange={setIsRecurring}
                id="recurring-mode"
              />
              <Label htmlFor="recurring-mode">Make this a recurring slot</Label>
            </div>

            {isRecurring && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="mb-2 block">Select days of the week</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {daysOfWeek.map((day) => (
                      <div key={day} className="flex items-center space-x-2">
                        <Checkbox
                          id={day}
                          checked={selectedDays.includes(day)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedDays([...selectedDays, day]);
                            } else {
                              setSelectedDays(selectedDays.filter(d => d !== day));
                            }
                          }}
                        />
                        <Label htmlFor={day}>{day}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block">Recurring until</Label>
                  <input
                    type="date"
                    className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white"
                    value={format(recurringEndDate, "yyyy-MM-dd")}
                    onChange={(e) => {
                      const date = new Date(e.target.value);
                      date.setHours(23, 59, 59, 999);
                      setRecurringEndDate(date);
                    }}
                    min={format(new Date(), "yyyy-MM-dd")}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-black mb-2 block">
              {renderTooltip(
                "Start Time *",
                "Choose when this listing will be available for pickup/delivery."
              )}
            </Label>
            <select
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-gray-200 bg-[#EDEDED] text-black"
            >
              {generateTimeOptions().map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label className="text-black mb-2 block">
              {renderTooltip(
                "End Time *",
                "Choose when this listing will no longer be available for pickup/delivery."
              )}
            </Label>
            <select
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-gray-200 bg-[#EDEDED] text-black"
            >
              {generateTimeOptions()
                .filter((time) => time > startTime)
                .map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
            </select>
          </div>

          <Button
            onClick={handleAddSlot}
            disabled={!dateRange?.from}
            className="w-full mt-4"
          >
            Add Availability Slot
          </Button>
        </div>
      </div>

      {selectedSlots.length > 0 && (
        <div className="mt-6">
          <Label className="text-black mb-2 block">Availability Slots</Label>
          <ScrollArea className="h-[200px] rounded-md border p-4">
            <div className="space-y-2">
              {selectedSlots.map((slot, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 rounded-lg bg-white border"
                >
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">
                      {formatSlotDisplay(slot)} • {slot.startTime} - {slot.endTime}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveSlot(index)}
                  >
                    <X className="h-4 w-4 text-gray-500" />
                  </Button>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}

      <div className="space-y-2.5 pt-4">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="limitedPortions"
            checked={hasLimitedPortions}
            onCheckedChange={(checked) => onHasLimitedPortionsChange?.(checked as boolean)}
          />
          <Label htmlFor="limitedPortions" className="text-black font-medium text-base">
            Limited portions available
          </Label>
        </div>

        {hasLimitedPortions && (
          <div className="mt-4">
            <Label className="text-black mb-2 block">Available Portions</Label>
            <Input
              type="number"
              value={availablePortions}
              onChange={(e) => onAvailablePortionsChange?.(e.target.value)}
              min="1"
              className="w-full h-10 px-3 rounded-md border border-gray-200 bg-[#EDEDED] text-black"
              placeholder="Enter number of portions"
            />
          </div>
        )}
      </div>
    </div>
  );
};
